import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  WorkOrderAssignmentPayoutRecord,
  workOrderAssignmentPayoutRecordQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderPayoutRecordQuery";
import { updateWorkOrderAssignmentPayout } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderPayoutMutations";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TaxTypeInput } from "@/app/Payments/Components/TaxTypeInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericFieldArray } from "@/lib/Components/Form/FieldArray/GenericFieldArray";
import { TaxType } from "@/gql/graphql";
import { Suspense } from "react";
import { toast } from "sonner";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { z } from "zod";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";

const lineItemSchema = z.object({
  id: z.string().nullish(),
  description: z.string().nullish(),
  tax_type: z.nativeEnum(TaxType),
  amount_inc_tax: z
    .number({ error: "Amount is required" })
    .int()
    .positive({ error: "Amount must be greater than zero" }),
});

const validation = z.object({
  lineItems: z.object({
    upsert: z.array(lineItemSchema).min(1, {
      error: "At least one line item is required",
    }),
    delete: z.array(z.string()),
  }),
});

type LineItemValue = {
  id?: string | null;
  description?: string | null;
  tax_type: TaxType;
  amount_inc_tax: number;
};

type FormValues = {
  lineItems: {
    upsert: LineItemValue[];
    delete: string[];
  };
};

export function EditPendingPayoutDialog({ payoutId }: { payoutId: string }) {
  return (
    <BaseDialog title="Edit Pending Payout" size="2xl">
      <Suspense fallback={<div className="p-4 text-center">Loading...</div>}>
        <EditPendingPayoutContent payoutId={payoutId} />
      </Suspense>
    </BaseDialog>
  );
}

function EditPendingPayoutContent({ payoutId }: { payoutId: string }) {
  const { data } = useSuspenseGqlQuery(workOrderAssignmentPayoutRecordQuery, {
    id: payoutId,
  });
  const { mutateAsync: updatePayout } = useGqlMutation(
    updateWorkOrderAssignmentPayout,
  );
  const { close } = useDialogState();

  const payout = data.workOrderAssignmentPayout;

  if (!payout) {
    return <div className="p-4">Payout not found</div>;
  }

  const initialValues: FormValues = {
    lineItems: {
      upsert: payout.lineItems.map((line) => ({
        id: line.id,
        description: line.description ?? "",
        tax_type: line.tax_type,
        amount_inc_tax: line.amount_inc_tax,
      })),
      delete: [],
    },
  };

  return (
    <div className="space-y-4">
      <GenericForm<FormValues>
        validate={toFormikValidate(validation)}
        initialValues={initialValues}
        onSubmit={async (values) => {
          await updatePayout({
            id: payout.id,
            input: {
              lineItems: {
                upsert: values.lineItems.upsert.map((line) => ({
                  id: line.id ?? null,
                  description: line.description ?? null,
                  tax_type: line.tax_type,
                  amount_inc_tax: line.amount_inc_tax,
                })),
                delete: values.lineItems.delete,
              },
            },
          });

          toast.success("Payout updated");
          close();
        }}
      >
        <GenericFieldArray<LineItemValue>
          label="Line items"
          fieldArrayKey="lineItems.upsert"
          lineItemLabel="line item"
          newLineItemValues={{
            description: "",
            tax_type: TaxType.NoGst,
            amount_inc_tax: null!,
          }}
          footer={<TotalsFooter payout={payout} />}
        >
          {(_item, index) => (
            <>
              <TextInput
                name={`lineItems.upsert.${index}.description`}
                label="Description"
                className="col-span-full sm:col-span-6"
              />
              <TaxTypeInput
                name={`lineItems.upsert.${index}.tax_type`}
                label="Tax"
                className="col-span-full sm:col-span-3"
              />
              <CurrencyInput
                name={`lineItems.upsert.${index}.amount_inc_tax`}
                label="Amount (inc. tax)"
                currency={payout.currency}
                className="col-span-full sm:col-span-3"
              />
            </>
          )}
        </GenericFieldArray>
        <DialogSubmitButton text="Save changes" />
      </GenericForm>
    </div>
  );
}

function TotalsFooter({ payout }: { payout: WorkOrderAssignmentPayoutRecord }) {
  return (
    <div className="text-sm text-zinc-600">
      Current total:{" "}
      <span className="font-medium text-zinc-900">
        {formatCurrency(payout.amount, payout.currency)}
      </span>{" "}
      <span className="text-xs text-zinc-500">
        (recalculated on save from line items)
      </span>
    </div>
  );
}
